import { useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { getMessages } from '../i18n';
import type { SurveySettings } from '../types/survey';

interface OperatorPanelProps {
  open: boolean;
  settings: SurveySettings;
  questionLimit: number;
  onClose: () => void;
  onApply: (settings: SurveySettings) => void;
  onReset: () => void;
}

export function OperatorPanel({
  open,
  settings,
  questionLimit,
  onClose,
  onApply,
  onReset,
}: OperatorPanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [draft, setDraft] = useState(settings);
  const messages = getMessages(draft.language).operator;
  const isRtl = draft.language === 'ar';

  useEffect(() => {
    if (open) {
      setDraft(settings);
      requestAnimationFrame(() => closeButtonRef.current?.focus());
    }
  }, [open, settings]);

  useGSAP(
    () => {
      const panel = panelRef.current;
      const backdrop = backdropRef.current;
      if (!panel || !backdrop) return;

      gsap.killTweensOf([panel, backdrop]);

      if (open) {
        gsap.set([panel, backdrop], { visibility: 'visible' });
        gsap.fromTo(
          backdrop,
          { opacity: 0 },
          { opacity: 1, duration: 0.24, ease: 'power2.out' },
        );
        gsap.fromTo(
          panel,
          { x: 0, xPercent: 115 },
          { x: 0, xPercent: 0, duration: 0.38, ease: 'power3.out' },
        );
        return;
      }

      gsap.to(backdrop, {
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in',
        onComplete: () => gsap.set(backdrop, { visibility: 'hidden' }),
      });
      gsap.to(panel, {
        x: 0,
        xPercent: 115,
        duration: 0.3,
        ease: 'power3.in',
        onComplete: () => gsap.set(panel, { visibility: 'hidden' }),
      });
    },
    { dependencies: [open] },
  );

  function update<K extends keyof SurveySettings>(key: K, value: SurveySettings[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  return (
    <>
      <div
        ref={backdropRef}
        className="operator-backdrop"
        aria-hidden="true"
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        className="operator-panel"
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-label={messages.dialogLabel}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <div className="operator-panel__header">
          <div>
            <span className="eyebrow">{messages.eyebrow}</span>
            <h2>{messages.title}</h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="icon-button"
            aria-label={messages.close}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <label className="field">
          <span>{messages.language}</span>
          <select
            value={draft.language}
            onChange={(event) => update('language', event.target.value as SurveySettings['language'])}
          >
            <option value="bilingual">{messages.languageBilingual}</option>
            <option value="fr">{messages.languageFrench}</option>
            <option value="ar">{messages.languageArabic}</option>
          </select>
        </label>

        <label className="field">
          <span>{messages.questionCount}</span>
          <input
            type="number"
            min="1"
            max={questionLimit}
            value={draft.questionCount}
            onChange={(event) => update('questionCount', Number(event.target.value))}
          />
        </label>

        <label className="field field--row">
          <span>{messages.shuffleQuestions}</span>
          <input
            type="checkbox"
            checked={draft.shuffleQuestions}
            onChange={(event) => update('shuffleQuestions', event.target.checked)}
          />
        </label>

        <label className="field field--row">
          <span>{messages.shuffleAnswers}</span>
          <input
            type="checkbox"
            checked={draft.shuffleAnswers}
            onChange={(event) => update('shuffleAnswers', event.target.checked)}
          />
        </label>

        <label className="field field--row">
          <span>{messages.autoAdvance}</span>
          <input
            type="checkbox"
            checked={draft.autoAdvance}
            onChange={(event) => update('autoAdvance', event.target.checked)}
          />
        </label>

        <label className="field">
          <span>{messages.feedbackDuration}</span>
          <input
            type="number"
            min="3"
            max="15"
            value={draft.feedbackDuration}
            onChange={(event) => update('feedbackDuration', Number(event.target.value))}
          />
        </label>

        <label className="field">
          <span>{messages.restartDuration}</span>
          <input
            type="number"
            min="5"
            max="30"
            value={draft.restartDuration}
            onChange={(event) => update('restartDuration', Number(event.target.value))}
          />
        </label>

        <div className="operator-panel__actions">
          <button type="button" className="button button--ghost" onClick={onReset}>
            {messages.reset}
          </button>
          <button type="button" className="button button--primary" onClick={() => onApply(draft)}>
            {messages.apply}
          </button>
        </div>
        <p className="operator-panel__note">{messages.resetShortcut}</p>
      </aside>
    </>
  );
}
